import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import { useAuth } from '../../components/shared/AuthContext';
import { PageLayout, Table, Button } from "../../components/ui";
import Loader from '../../components/ui/Loader';
import { ReportHeader, ReportSummaryCard } from "../../components/reports";

const fmt = (n) => (n != null && n !== "" ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—");
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2,"0")}-${String(dt.getMonth()+1).padStart(2,"0")}-${dt.getFullYear()}`;
  } catch {
    return d;
  }
};

const COLUMNS = [
  { key: "sno",               label: "#",                     align: "center" },
  { key: "branchName",        label: "Branch Name",           align: "left"   },
  { key: "branchCode",        label: "Branch Code",           align: "left"   },
  { key: "region",            label: "Region",                align: "left"   },
  { key: "division",          label: "Division",              align: "left"   },
  { key: "customerName",      label: "Customer Name",         align: "left"   },
  { key: "prospectId",        label: "Prospect ID",           align: "left"   },
  { key: "contactNo",         label: "Contact No",            align: "left"   },
  { key: "ApplicationId",     label: "Application ID",        align: "left"   },
  { key: "funderName1",       label: "Funding Agency 1",      align: "left"   },
  { key: "funderName2",       label: "Funding Agency 2",      align: "left"   },
  { key: "funderName3",       label: "Funding Agency 3",      align: "left"   },
  { key: "customerRequestedAmount", label: "Customer Requested", align: "right" },
  { key: "requestedAmount",   label: "Approved Amount",       align: "right"  },
  { key: "disbursedAmount",   label: "Disbursed Amount",      align: "right"  },
  { key: "tenureInMonths",    label: "Tenure (Months)",       align: "center" },
  { key: "emiAmount",         label: "EMI Amount",            align: "right"  },
  { key: "rateOfInterest",    label: "ROI %",                 align: "center" },
  { key: "dueDate",           label: "Due Date",              align: "center" },
  { key: "processingFee",     label: "Processing Fee",        align: "right"  },
  { key: "gstOnProcessingFee", label: "GST",                  align: "right"  },
  { key: "insurance",         label: "Insurance",             align: "right"  },
  { key: "securityDeposit",   label: "Security Deposit",      align: "right"  },
  { key: "aadharNumber",      label: "Aadhar Number",         align: "left"   },
  { key: "panNumber",         label: "Pan Number",            align: "left"   },
  { key: "voterId",           label: "Voter Id",              align: "left"   },
  { key: "bankAccountNumber", label: "Bank Account Number",   align: "left"   },
  { key: "ifscCode",          label: "IFSC Code",             align: "left"   },
  { key: "bankName",          label: "Bank Name",             align: "left"   },
  { key: "bankBranch",        label: "Bank Branch",           align: "left"   },
  { key: "employeeName",      label: "Employee Name",         align: "left"   },
  { key: "employeeCode",      label: "Employee Code",         align: "center" },
  { key: "disbursementDate",  label: "Disbursement Date",     align: "left"   },
];

const CURRENCY_KEYS = new Set([
  "customerRequestedAmount","requestedAmount","disbursedAmount","emiAmount",
  "processingFee","gstOnProcessingFee","insurance","securityDeposit",
]);
const DATE_KEYS = new Set(["disbursementDate", "createdAt"]);

const DisbursedAmountReport = () => {
  const { roleName, data: authData } = useAuth();
  const isSuperAdmin = roleName === "superadmin";
  const userBranchId = authData?.branchId;
  const shouldFilterByBranch = !isSuperAdmin && userBranchId;

  const [loading,              setLoading]              = useState(false);
  const [error,                setError]                = useState(null);
  const [rows,                 setRows]                 = useState([]);
  const [branches,             setBranches]             = useState([]);
  const [fieldOfficers,        setFieldOfficers]        = useState([]);
  const [selectedBranch,       setSelectedBranch]       = useState(shouldFilterByBranch ? String(userBranchId) : "");
  const [selectedFieldOfficer, setSelectedFieldOfficer] = useState("");
  const [search,               setSearch]               = useState("");
  const [rowsPerPage,          setRowsPerPage]          = useState(10);
  const [currentPage,          setCurrentPage]          = useState(1);

  // Load branches
  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/getModelData/branch_details`).then((res) => {
      let list = res.data?.data || [];
      if (shouldFilterByBranch) list = list.filter((b) => String(b.id) === String(userBranchId));
      setBranches(list);
    }).catch(console.error);
  }, [shouldFilterByBranch, userBranchId]);

  // Load field officers when branch changes
  useEffect(() => {
    if (!selectedBranch) { setFieldOfficers([]); return; }
    axios.get(`${API_BASE_URL}/api/getModelData/staff_details?filter={"branchId":${selectedBranch}}`).then((res) => {
      const staff = res.data?.data || [];
      setFieldOfficers(staff.filter((s) => [2,3,4,5].includes(Number(s.roleId))));
    }).catch(console.error);
  }, [selectedBranch]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (selectedBranch) params.branchId = selectedBranch;
      else if (shouldFilterByBranch) params.branchId = userBranchId;
      if (selectedFieldOfficer) params.fieldOfficerId = selectedFieldOfficer;

      const res = await axios.get(`${API_BASE_URL}/api/getDisbursedAmountReportOptimized`, { params });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setError("Failed to load report. Please try again.");
    }
    setLoading(false);
  }, [selectedBranch, selectedFieldOfficer, shouldFilterByBranch, userBranchId]);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    return (
      (r.customerName  || "").toLowerCase().includes(q) ||
      (r.prospectId    || "").toLowerCase().includes(q) ||
      (r.ApplicationId || "").toLowerCase().includes(q) ||
      (r.branchName    || "").toLowerCase().includes(q) ||
      (r.employeeName  || "").toLowerCase().includes(q) ||
      String(r.contactNo || "").includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated  = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // Summary
  const totalDisbursed = filtered.reduce((s, r) => s + (Number(r.disbursedAmount) || 0), 0);

  const exportCSV = () => {
    const headers = COLUMNS.map((c) => c.label);
    const csvRows = [
      headers.join(","),
      ...filtered.map((r, i) =>
        COLUMNS.map((c) => {
          const val = c.key === "sno" ? i + 1 : String(r[c.key] ?? "").replace(/"/g, '""');
          return String(val).includes(",") ? `"${val}"` : val;
        }).join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "disbursed_amount_report.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const renderCell = (r, col, i) => {
    const val = r[col.key];
    if (col.key === "sno") return <span className="text-ink-400">{(currentPage - 1) * rowsPerPage + i + 1}</span>;
    if (DATE_KEYS.has(col.key)) return fmtDate(val);
    if (CURRENCY_KEYS.has(col.key)) return val != null && val !== "" ? <span>₹{fmt(val)}</span> : "—";
    if (col.key === "customerName") return <span className="font-semibold text-ink-900">{val || "—"}</span>;
    if (col.key === "ApplicationId") return <code className="text-xs font-mono text-primary font-bold">{val || "—"}</code>;
    return val != null && val !== "" ? val : "—";
  };

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-4">
      {/* Top Filter and Actions Bar using reusable ReportHeader */}
      <ReportHeader
        filters={
          <>
            {/* Branch */}
            <div className="relative min-w-[160px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Branch
              </label>
              <div className="relative">
                <select
                  value={selectedBranch}
                  onChange={(e) => { setSelectedBranch(e.target.value); setSelectedFieldOfficer(""); setCurrentPage(1); }}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="">All Branches</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Staff */}
            <div className="relative min-w-[160px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Staff
              </label>
              <div className="relative">
                <select
                  value={selectedFieldOfficer}
                  onChange={(e) => { setSelectedFieldOfficer(e.target.value); setCurrentPage(1); }}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  <option value="">All Staff</option>
                  {fieldOfficers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Rows/Page */}
            <div className="relative min-w-[100px] mt-1">
              <label className="absolute -top-2 left-3 bg-white px-1.5 text-xs font-bold tracking-wide text-ink-400 select-none z-10 pointer-events-none">
                Rows/Page
              </label>
              <div className="relative">
                <select
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="form-input bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary/20 text-ink-700 h-[38px] text-xs pl-3.5 pr-8 w-full appearance-none cursor-pointer focus:outline-none transition-all font-medium"
                >
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <div className="mt-1">
              <Button onClick={loadData} variant="secondary" className="h-[38px] px-3 font-semibold text-xs">
                Apply Filters
              </Button>
            </div>

            {/* Search Input */}
            <div className="min-w-[240px] mt-1">
              <input
                placeholder="Search customer / ID / branch / employee..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="w-full bg-white rounded-lg border border-border-medium hover:border-primary focus:border-primary text-ink-700 h-[38px] text-xs px-3.5 focus:outline-none transition-all font-medium"
              />
            </div>
          </>
        }
        actions={
          <Button
            onClick={exportCSV}
            variant="primary"
            size="sm"
            className="shadow-sm h-[38px] px-4 font-bold"
          >
            <i className="fa-solid fa-file-csv mr-1.5"></i>
            Export CSV
          </Button>
        }
      />
      {loading && !rows.length && <Loader message="Loading disbursement data..." />}

      {error && (
        <div className="bg-rose-50 text-rose-700 p-4 rounded-xl border border-rose-200 text-sm font-medium mb-4">
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <ReportSummaryCard
          label="Total Records"
          value={filtered.length}
          bg="bg-[#D9F3F7]"
          border="border-[#0891B2]/25"
          accent="text-[#0891B2]"
          icon="fa-file-lines"
        />
        <ReportSummaryCard
          label="Total Disbursed"
          value={`₹${fmt(totalDisbursed)}`}
          bg="bg-[#FFF0C7]"
          border="border-[#D97706]/25"
          accent="text-[#D97706]"
          icon="fa-money-bill-wave"
        />
      </div>

      {/* Main Table */}
      <div className="flex-grow flex flex-col min-h-0">
        <Table className="min-w-[3200px]">
          <Table.Head>
            <tr>
              {COLUMNS.map((c) => (
                <Table.HeadCell key={c.key} align={c.align} className="whitespace-nowrap">
                  {c.label}
                </Table.HeadCell>
              ))}
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <Table.Row>
                <Table.Cell colSpan={COLUMNS.length} align="center" className="py-10">
                  <div className="flex flex-col items-center justify-center py-4">
                    <Loader size="md" />
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : paginated.length === 0 ? (
              <Table.EmptyRow colSpan={COLUMNS.length} message="No disbursement records found." />
            ) : (
              paginated.map((r, i) => (
                <Table.Row key={r.id || i}>
                  {COLUMNS.map((col) => (
                    <Table.Cell key={col.key} align={col.align} className="whitespace-nowrap">
                      {renderCell(r, col, i)}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between mt-4 bg-white p-3 rounded-xl border border-border-light shadow-sm">
            <span className="text-xs text-ink-500">
              Showing {paginated.length} of {filtered.length} records
            </span>
            <div className="flex gap-1 items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-xs text-ink-500 px-3 font-semibold">
                Page {currentPage} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default DisbursedAmountReport;